import { ActivityIndicator, Platform, Pressable } from 'react-native';

import { RefreshCw as RefreshIcon } from 'lucide-react-native';
import { useGlassAccentColor } from '@/components/glass-surface';

// Web-only refresh affordance (issue #183): react-native-web renders
// RefreshControl as a bare View and drops onRefresh entirely, so every
// pull-to-refresh in the app is silently inert on the Netlify build.
// Renders nothing on native -- there the gesture is the affordance --
// and on web gives the list's own refetch a visible, working control.
//
// Redesigned from a centered "Refresh" text link to a small icon-only
// button, per the 2026-09-18 full-app critique (issue #290): the old
// text link sat above/outside every screen's own heading row --
// literally the topmost element on Threads, above the page's own
// identity -- and read as debug scaffolding left in the build, not a
// real product control. This component no longer owns its own
// placement; call sites now render it INLINE within their existing
// heading row (see threads/index.tsx, matches/index.tsx,
// thread-detail/index.tsx for the pattern), same 24x24 minimum touch
// target as every other icon button in the app, never centered on its
// own row above the H1.
export function WebRefreshButton({ onRefresh, refreshing }: { onRefresh: () => void; refreshing: boolean }) {
  const accentColor = useGlassAccentColor();

  if (Platform.OS !== 'web') return null;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={refreshing ? 'Refreshing' : 'Refresh'}
      accessibilityState={{ busy: refreshing }}
      aria-busy={refreshing}
      style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}
      onPress={() => {
        if (!refreshing) onRefresh();
      }}
    >
      {refreshing ? (
        <ActivityIndicator size="small" color={accentColor} />
      ) : (
        <RefreshIcon size={20} strokeWidth={1.75} color={accentColor} />
      )}
    </Pressable>
  );
}
